import { ModulesType } from '@lib/sanity/queries/modules/allModules';
import { HomePageSchemaType } from '@lib/sanity/queries/pages/homePage/types';
import { LandingPagesSchemaType } from '@lib/sanity/queries/pages/landingPages/types';
import { PricingPageSchemaType } from '@lib/sanity/queries/pages/pricingPage/types';
import {
  ButtonProjection,
  IconButtonProjection,
  PortableTextProjection,
  StringProjection,
  UnicaseProjection,
} from '@lib/sanity/types/common';
import { HeaderProps } from '@lib/types';

export function isButtonExist(
  button?: IconButtonProjection | ButtonProjection,
): button is IconButtonProjection | ButtonProjection {
  const isIconExist = () => {
    const iconButton = button as IconButtonProjection;
    return iconButton?.icon?.icon?.asset?.url || iconButton?.icon?.iconSvg?.result;
  };

  return !!((button?.text?.value || isIconExist()) && button?.url);
}

export function isPortableText(data?: PortableTextProjection | StringProjection) {
  return !!(data?.value && data?.value?.length && Array.isArray(data?.value));
}

function hasUnicase(data?: UnicaseProjection) {
  return !!(
    data?.breadcrumbs?.first?.text?.value ||
    data?.breadcrumbs?.second?.text?.value ||
    data?.breadcrumbs?.third?.text?.value ||
    data?.unicase?.value
  );
}

function hasButtonLink(data?: StringProjection | ButtonProjection) {
  return (data as StringProjection)?.value || (data as ButtonProjection)?.text?.value;
}

export type ElementsToInclude = (keyof Omit<HeaderProps, 'className'>)[];

export const isWindowDefined = typeof window !== 'undefined';
export const isLocationDefined = typeof location !== 'undefined';
export const isNavigatorDefined = typeof navigator !== 'undefined';
export const isDocumentDefined = typeof document !== 'undefined';

export function isElementExistInHeader(
  data?: HeaderProps,
  elementsToInclude: ElementsToInclude = [
    'unicase',
    'title',
    'description',
    'buttonPrimary',
    'buttonSecondary',
    'buttonLink',
    'embedButton',
  ],
) {
  if (!data) {
    return false;
  }

  return !!(
    (elementsToInclude.includes('unicase') && hasUnicase(data.unicase as UnicaseProjection)) ||
    (elementsToInclude.includes('title') &&
      (isPortableText(data.title as PortableTextProjection) || (data.title as StringProjection)?.value)) ||
    (elementsToInclude.includes('description') &&
      (isPortableText(data.description as PortableTextProjection) || (data.description as StringProjection)?.value)) ||
    (elementsToInclude.includes('buttonPrimary') && data.buttonPrimary?.text?.value) ||
    (elementsToInclude.includes('buttonSecondary') && data.buttonSecondary?.text?.value) ||
    (elementsToInclude.includes('buttonLink') && hasButtonLink(data.buttonLink)) ||
    (elementsToInclude.includes('embedButton') && (data.embedButton as string))
  );
}

export function isMissingTranslation(
  lang: string,
  data?: LandingPagesSchemaType | HomePageSchemaType | PricingPageSchemaType,
) {
  // page doesn't exist
  if (!data || lang === 'en') {
    return false;
  }

  const { title, description } = data.seo ?? {};
  if (!title?.value || !description?.value) {
    return true;
  }

  const modules = data.modules as ModulesType[];
  return !modules?.some((module) => module?.isTranslated === true);
}
